import { useState, useEffect, useCallback } from 'react';
import { api } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';

interface PixRequest {
  amount: number;
  pix_key: string;
  description: string;
}

interface PixResponse {
  success: boolean;
  transaction_id?: string;
  status?: string;
  message?: string;
  data?: {
    amount: number;
    pix_key: string;
    description: string;
    status: string;
    qr_code: string;
    qr_code_text: string;
  };
  qr_code?: string;
  qr_code_text?: string;
}

interface PixHistoryItem {
  id: number;
  amount: number;
  pix_key: string;
  status: 'pending' | 'completed' | 'failed';
  created_at: string;
  transaction_id?: string;
}

interface PixHistoryResponse {
  success: boolean;
  data: PixHistoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface UserResponse {
  pix_key?: string;
  email: string;
}

export const usePixPayment = () => {
  const { decodedToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'pending' | 'completed' | 'failed'>('idle');
  const [currentTransaction, setCurrentTransaction] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrCodeText, setQrCodeText] = useState<string | null>(null);
  const [userPixKey, setUserPixKey] = useState<string>('');

  // Buscar dados do usuário para obter chave PIX
  const fetchUserData = useCallback(async () => {
    try {
      const response = await api.get<UserResponse>(`/user/get/${decodedToken.id}`);
      const userData = response.data;
      
      // Usar chave PIX do usuário ou email como fallback
      const pixKey = userData.pix_key || userData.email;
      setUserPixKey(pixKey);
      
      return pixKey;
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      // Fallback para email do token decodificado
      setUserPixKey(decodedToken.email);
      return decodedToken.email;
    }
  }, [decodedToken.id, decodedToken.email]);

  // Criar cobrança PIX
  const createPixPayment = useCallback(async (amount: number, description: string) => {
    try {
      setLoading(true);
      setPaymentStatus('pending');

      const pixKey = await fetchUserData();
      
      const pixRequest: PixRequest = {
        amount,
        pix_key: pixKey,
        description
      };

      const response = await api.post<PixResponse>('/pix', pixRequest);
      
      if (response.data.success && response.data.transaction_id) {
        setCurrentTransaction(response.data.transaction_id);
        
        // Handle both old and new API response formats
        const qrCodeData = response.data.data || response.data;
        setQrCode(qrCodeData.qr_code || null);
        setQrCodeText(qrCodeData.qr_code_text || null);
        
        toast.success(response.data.message || 'Cobrança PIX criada com sucesso!');
        return {
          success: true,
          transactionId: response.data.transaction_id,
          qrCode: qrCodeData.qr_code,
          qrCodeText: qrCodeData.qr_code_text
        };
      } else {
        throw new Error(response.data.message || 'Erro ao criar cobrança PIX');
      }
    } catch (error: any) {
      console.error('Erro ao criar pagamento PIX:', error);
      setPaymentStatus('failed');
      
      const errorMessage = error.response?.data?.message || 'Erro ao processar pagamento PIX';
      toast.error(errorMessage);
      
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [fetchUserData]);

  // Verificar status do pagamento
  const checkPaymentStatus = useCallback(async (transactionId: string) => {
    try {
      const response = await api.get<PixHistoryResponse>('/pix/history', {
        params: {
          page: 1,
          limit: 50,
          status: 'completed'
        }
      });

      if (response.data.success) {
        const completedTransaction = response.data.data.find(
          item => item.transaction_id === transactionId && item.status === 'completed'
        );

        if (completedTransaction) {
          setPaymentStatus('completed');
          setCurrentTransaction(null);
          setQrCode(null);
          setQrCodeText(null);
          
          toast.success('Pagamento confirmado com sucesso!');
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Erro ao verificar status do pagamento:', error);
      return false;
    }
  }, []);

  // Verificar pagamento automaticamente a cada 30 segundos
  useEffect(() => {
    if (currentTransaction && paymentStatus === 'pending') {
      const interval = setInterval(async () => {
        const isCompleted = await checkPaymentStatus(currentTransaction);
        if (isCompleted) {
          clearInterval(interval);
        }
      }, 30000); // 30 segundos

      return () => clearInterval(interval);
    }
  }, [currentTransaction, paymentStatus, checkPaymentStatus]);

  // Reset do estado
  const resetPayment = useCallback(() => {
    setPaymentStatus('idle');
    setCurrentTransaction(null);
    setQrCode(null);
    setQrCodeText(null);
  }, []);

  return {
    loading,
    paymentStatus,
    currentTransaction,
    qrCode,
    qrCodeText,
    userPixKey,
    createPixPayment,
    checkPaymentStatus,
    resetPayment,
    fetchUserData
  };
};
